














import debounce from 'debounce';
import { Editor as TipTapEditor, EditorContent } from '@tiptap/vue-2';
import Focus from '@tiptap/extension-focus';
import Text from '@tiptap/extension-text';
import type { Field, MergeValues } from '@getaccept/editor-lib';
import {
  ProsemirrorHelper,
  InputDocNode,
  MergeValueNode,
  ParagraphNode,
  EditorHelper,
} from '@getaccept/editor-lib';
import type { PropType, Ref } from '@vue/composition-api';
import {
  onBeforeUnmount,
  onMounted,
  computed,
  defineComponent,
  ref,
  watch,
} from '@vue/composition-api';
import { t } from '@getaccept/lib-shared/src/helpers/translation.helper';
import Placeholder from '@tiptap/extension-placeholder';
import { validateEmail } from '@getaccept/lib-shared/src/helpers';
import { IconSize } from '@getaccept/lib-ui';
import type { EditorRecipientInput } from '@getaccept/lib-shared/src/types/editor-recipient-input';

export default defineComponent({
  components: { EditorContent },
  props: {
    inputField: { type: Object as PropType<Field>, required: true },
    recipientInput: { type: Object as PropType<EditorRecipientInput>, default: null },
    fieldFocused: { type: Boolean },
    editable: { type: Boolean },
    placeholder: { type: String, default: '' },
    isUpdating: { type: Boolean },
    isRequired: { type: Boolean },
    mergeValues: { type: Object as PropType<MergeValues>, required: true },
    fields: { type: Array as PropType<Field[]>, default: () => [] },
    hasUpdateError: { type: Boolean },
  },
  setup(props, { emit }) {
    const editor: Ref<TipTapEditor | null> = ref(null);
    const emailErrorMessage = ref('');

    const hasError = computed(() => props.hasUpdateError || !!emailErrorMessage.value);
    const errorMessage = computed(() => emailErrorMessage.value || '');

    const plainContent = computed(() =>
      editor.value ? ProsemirrorHelper.getInnerTextContent(editor.value.state).trim() : ''
    );

    const update = () => {
      if (!props.editable) {
        return;
      }

      const isValidEmail =
        (!props.isRequired && plainContent.value === '') || validateEmail(plainContent.value);
      if (!isValidEmail) {
        emailErrorMessage.value = 'input_fields_error_field_not_correctly_filled_out';
        emit('validate-form-input', false);
        return;
      }

      emailErrorMessage.value = '';
      emit('update', plainContent.value);
      emit('validate-form-input', true);
    };

    const debounceUpdate = debounce(update, 300);

    watch(
      () => props.fieldFocused,
      () => {
        if (props.fieldFocused) {
          editor.value.view.focus();
        }
      }
    );

    watch(
      () => props.recipientInput,
      () => {
        if (!props.recipientInput) {
          return;
        }

        const content = ProsemirrorHelper.getInputContent(props.recipientInput.value, props.fields);
        editor.value.commands.setContent(content);
      }
    );

    watch(
      () => props.editable,
      () => {
        editor.value.setEditable(props.editable);
      }
    );

    const initialContent = () => {
      const inputField = EditorHelper.hasMergeTags(props.inputField.value)
        ? ProsemirrorHelper.getMergeValueFromMergeTag(
            ProsemirrorHelper.getTagIdFromMergeTag(props.inputField.value),
            props.fields,
            props.mergeValues
          )
        : props.inputField.value;
      return ProsemirrorHelper.getInputContent(
        props.recipientInput?.value || inputField,
        props.fields
      );
    };

    const editorConfig = computed(() => ({
      content: initialContent(),
      editable: props.editable,
      enableInputRules: false,
      enablePasteRules: false,
      extensions: [
        InputDocNode,
        Focus,
        ParagraphNode,
        Text,
        MergeValueNode,
        Placeholder.configure({
          placeholder: t('input_fields_add_email'),
        }),
      ],
      onBlur: update,
      onUpdate: debounceUpdate,
    }));

    onMounted(() => {
      editor.value = new TipTapEditor(editorConfig.value);
    });

    onBeforeUnmount(() => {
      editor.value.destroy();
    });

    return {
      IconSize,
      editor,
      errorMessage,
      emailErrorMessage,
      hasError,
      update,
      t,
    };
  },
});
